import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { api } from '../../api/cliente';
import { Carregando, Cartao, Indicador, Vazio } from '../../componentes/base';
import { Paginacao, type Paginado } from '../../componentes/Paginacao';
import { data as formatarData, mesPorExtenso, moeda, plural } from '../../util/formato';
import { useSessaoPortal } from '../SessaoPortal';

interface PagamentoDoPortal {
  readonly id: string;
  readonly devedor: { id: string; nome: string };
  readonly clinica: { nome: string } | null;
  readonly numero: number;
  readonly quantidadeParcelas: number;
  readonly valorPago: number;
  readonly dataPagamento: string | null;
}

function mesAtual(): string {
  const hoje = new Date();
  return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Quem pagou no mês.
 *
 * É a resposta ao clique em "recuperado no mês": total que não se abre é uma
 * afirmação que ninguém consegue conferir. A clínica vê nome por nome de onde vem
 * aquele número — e é a mesma decisão já tomada do lado do escritório.
 *
 * Mostra o que foi PAGO, e nada sobre o que o escritório cobra: honorário aparece
 * consolidado na tela inicial, e mensalidade e custas não entram no portal.
 */
export function PaginaRecebimentosDoPortal() {
  const { token = '' } = useParams();
  const [params] = useSearchParams();
  const [pagina, setPagina] = useState(1);
  const { clinicas } = useSessaoPortal();

  const mes = params.get('mes') ?? mesAtual();
  const base = `/portal/${token}`;

  const { data: lista, isLoading } = useQuery({
    queryKey: ['portal', 'recebimentos', mes, pagina],
    queryFn: () =>
      api.buscar<Paginado<PagamentoDoPortal>>('/portal/pagamentos', {
        mes,
        pagina,
        limite: 50,
      }),
  });

  const itens = lista?.itens ?? [];
  /* A soma DESTA página, e o rótulo diz isso. Chamar de "total do mês" seria mentira
     assim que a lista passasse de cinquenta linhas. */
  const somaDaPagina = itens.reduce((s, i) => s + i.valorPago, 0);
  const variasCarteiras = clinicas.length > 1;

  return (
    <>
      <Link
        to={base}
        className="text-xs text-[color:var(--texto-suave)] hover:text-[color:var(--acento)]"
      >
        ← Voltar ao início
      </Link>

      <header className="pt-3 pb-6">
        <h1 className="text-[1.75rem] leading-tight text-[color:var(--texto)]">
          Recebido em {mesPorExtenso(mes).toLowerCase()}
        </h1>
        <p className="mt-1 text-sm text-[color:var(--texto-suave)]">
          Pagamentos dos seus clientes registrados no período
        </p>
      </header>

      {isLoading ? (
        <Carregando />
      ) : itens.length === 0 ? (
        <Vazio titulo={`Nenhum pagamento registrado em ${mesPorExtenso(mes).toLowerCase()}.`} />
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Indicador
              rotulo={
                (lista?.totalPaginas ?? 1) > 1
                  ? 'Soma desta página'
                  : `Recebido em ${mesPorExtenso(mes).toLowerCase()}`
              }
              valor={moeda(somaDaPagina)}
              tom="recebido"
              nota={plural(itens.length, 'pagamento', 'pagamentos')}
            />
          </div>

          <Cartao semPadding>
            <div className="rolagem-lateral">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-[color:var(--superficie-afundada)] text-left text-[0.6875rem] uppercase tracking-[0.08em] text-[color:var(--texto-tenue)]">
                    <th className="px-5 py-2.5 font-medium">Cliente</th>
                    {variasCarteiras && <th className="px-3 py-2.5 font-medium">Clínica</th>}
                    <th className="px-3 py-2.5 font-medium">Parcela</th>
                    <th className="px-3 py-2.5 font-medium">Data</th>
                    <th className="px-5 py-2.5 text-right font-medium">Valor pago</th>
                  </tr>
                </thead>
                <tbody>
                  {itens.map((p) => (
                    <tr
                      key={p.id}
                      className="border-b transition-colors last:border-0 hover:bg-[color:var(--superficie-afundada)]"
                    >
                      <td className="px-5 py-3">
                        <Link
                          to={`${base}/clientes/${p.devedor.id}`}
                          className="font-medium text-[color:var(--texto)] hover:text-[color:var(--acento)]"
                        >
                          {p.devedor.nome}
                        </Link>
                      </td>
                      {variasCarteiras && (
                        <td className="px-3 py-3 text-xs text-[color:var(--texto-suave)]">
                          {p.clinica?.nome ?? '—'}
                        </td>
                      )}
                      <td className="tabular px-3 py-3 whitespace-nowrap text-[color:var(--texto-suave)]">
                        {p.numero === 0
                          ? 'Entrada'
                          : `${p.numero} de ${p.quantidadeParcelas}`}
                      </td>
                      <td className="tabular px-3 py-3 whitespace-nowrap text-[color:var(--texto-suave)]">
                        {formatarData(p.dataPagamento)}
                      </td>
                      <td className="tabular px-5 py-3 text-right whitespace-nowrap">
                        {moeda(p.valorPago)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {lista && (
              <Paginacao
                dados={lista}
                aoMudarPagina={setPagina}
                nomeDoItem={['pagamento', 'pagamentos']}
              />
            )}
          </Cartao>
        </div>
      )}
    </>
  );
}
